// UI Framework — render UI from Module Registry schema.
// Theme tokens, generic Slider/Button/Debug components.
// DOES NOT know which module is running — only renders from schema.
// R15 (2026-09-22): Single LIGHT theme only — removed dark theme. Theme switcher not needed.

import { THEME } from './ui-theme';
export { THEME, createThemeSwitcher, injectTheme } from './ui-theme';
export type { ThemeSwitcherHandle } from './ui-theme';
export { API_BASE, apiAuthHeaders, ApiError, cancelPreview, fetchPreview, newPreviewController } from './api-client';
export { createHealthLeds, createModelHealthLeds, startHealthPoll } from './ui-health';
export type { HealthLeds, ModelHealthLeds } from './ui-health';
export { createButton, createSlider, getAppQuality, getGlobalSliderMode, onAppQualityChange, onSliderModeChange, setAppQuality, setAppQualityTriangleCount, setGlobalSliderMode } from './ui-controls';
export type { AppQuality } from './ui-controls';
export type { SliderCallbacks } from './ui-controls';
export { getExportFormat, onExportFormatChange, renderSidebar, setExportFormat } from './ui-sidebar';
export type { SidebarHandle } from './ui-sidebar';



// ---- Debug Log Viewer ----
export interface DebugLogHandle {
  el: HTMLElement;
  log: (msg: string, type?: 'info' | 'error' | 'warn') => void;
  clear: () => void;
}

export function createDebugLog(): DebugLogHandle {
  const el = document.createElement('div');
  el.id = 'debug-log';
  el.style.cssText = `
    position: absolute;
    bottom: 12px;
    right: 12px;
    width: 350px;
    max-height: 200px;
    background: rgba(30, 30, 30, 0.95);
    border: 1px solid #444;
    border-radius: 6px;
    font-family: var(--mono-font), monospace;
    font-size: 10px;
    color: #ccc;
    z-index: 25;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `;

  // Tab bar
  const tabBar = document.createElement('div');
  tabBar.style.cssText = 'display:flex;gap:0;border-bottom:1px solid #444;background:#1a1a1a;';
  
  const logTab = document.createElement('button');
  logTab.textContent = '📋 Log';
  logTab.style.cssText = 'flex:1;padding:6px;border:none;background:#333;color:#aaa;cursor:pointer;font-size:10px;border-radius:0;';
  
  const copyBtn = document.createElement('button');
  copyBtn.textContent = '📋 Copy';
  copyBtn.style.cssText = 'flex:1;padding:6px;border:none;background:#1a1a1a;color:#aaa;cursor:pointer;font-size:10px;border-radius:0;border-left:1px solid #444;';
  
  tabBar.appendChild(logTab);
  tabBar.appendChild(copyBtn);
  el.appendChild(tabBar);

  // Log content
  const logContent = document.createElement('div');
  logContent.style.cssText = 'flex:1;overflow-y:auto;padding:8px;';
  el.appendChild(logContent);

  const logs: Array<{msg: string, type: string}> = [];

  const log = (msg: string, type: 'info' | 'error' | 'warn' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const entry = `[${timestamp}] ${msg}`;
    logs.push({msg: entry, type});

    const line = document.createElement('div');
    line.style.cssText = `
      margin-bottom: 4px;
      color: ${type === 'error' ? '#ff6b6b' : type === 'warn' ? '#ffd93d' : '#6bff6b'};
      word-break: break-all;
    `;
    line.textContent = entry;
    logContent.appendChild(line);

    // Auto-scroll to bottom
    logContent.scrollTop = logContent.scrollHeight;

    // Keep last 50 logs
    if (logs.length > 50) {
      logs.shift();
      logContent.removeChild(logContent.firstChild!);
    }
  };

  const clear = () => {
    logs.length = 0;
    logContent.innerHTML = '';
  };

  copyBtn.addEventListener('click', () => {
    const text = logs.map(l => l.msg).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      copyBtn.style.background = '#2d5a2d';
      setTimeout(() => {
        copyBtn.style.background = '#1a1a1a';
      }, 500);
    });
  });

  // Intercept console logs
  const originalLog = console.log;
  const originalError = console.error;
  const originalWarn = console.warn;

  console.log = (...args: any[]) => {
    originalLog(...args);
    log(args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' '), 'info');
  };

  console.error = (...args: any[]) => {
    originalError(...args);
    log(args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' '), 'error');
  };

  console.warn = (...args: any[]) => {
    originalWarn(...args);
    log(args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' '), 'warn');
  };

  // Catch global errors
  window.addEventListener('error', (e) => {
    log(`ERROR: ${e.message} at ${e.filename}:${e.lineno}`, 'error');
  });

  return { el, log, clear };
}
export function createStatusBar(): { el: HTMLElement; set: (msg: string) => void; setError: (msg: string) => void; setOk: (msg: string) => void } {
  const el = document.createElement('div');
  el.id = 'status';
  el.textContent = 'Ready';
  return {
    el,
    set: (m) => { el.textContent = m; el.style.color = ''; },
    setError: (m) => { el.textContent = `❌ ${m}`; el.style.color = 'var(--error)'; },
    setOk: (m) => { el.textContent = `✅ ${m}`; el.style.color = 'var(--success)'; },
  };
}


// ---- View mode switcher (Shape / Wireframe / Overlay / X-Ray / Clay / Flat / Normals) ----
export type ViewMode = 'shape' | 'wireframe' | 'overlay' | 'xray' | 'clay' | 'flat' | 'normals';

export interface ViewSwitcherHandle {
  el: HTMLElement;
  mode: ViewMode;
  setOnChange: (cb: (m: ViewMode) => void) => void;
  setMode: (m: ViewMode) => void;
  setOnGridToggle: (cb: (on: boolean) => void) => void;
}

export function createViewSwitcher(initial: ViewMode = 'shape'): ViewSwitcherHandle {
  let onChange: ((m: ViewMode) => void) | null = null;
  let onGridToggle: ((on: boolean) => void) | null = null;
  const modes: ViewMode[] = ['shape', 'wireframe', 'overlay', 'xray', 'normals'];
  const labels: Record<ViewMode, string> = {
    shape: 'Solid',
    wireframe: 'Wire',
    overlay: 'Overlay',
    xray: 'X-Ray',
    clay: 'Clay',
    flat: 'Flat',
    normals: 'Normal',
  };

  const el = document.createElement('div');
  el.id = 'view-switcher';
  el.style.cssText = 'position:absolute;top:12px;right:12px;display:flex;gap:4px;background:rgba(255,255,255,0.85);padding:4px;border-radius:6px;z-index:10;backdrop-filter:blur(4px);border:1px solid var(--border);';

  const buttons: Record<ViewMode, HTMLButtonElement> = {} as any;
  modes.forEach((m) => {
    const b = document.createElement('button');
    b.textContent = labels[m];
    b.dataset.mode = m;
    b.style.cssText = 'padding:6px 12px;font-size:12px;margin:0;border:none;border-radius:4px;cursor:pointer;background:transparent;color:var(--text-muted);transition:all 0.15s;font-family:var(--mono-font);';
    b.addEventListener('click', () => {
      handle.setMode(m);
      if (onChange) onChange(m);
    });
    buttons[m] = b;
    el.appendChild(b);
  });

  // Grid toggle separator + button (R15: default OFF — subtle, not obtrusive)
  const sep = document.createElement('div');
  sep.style.cssText = 'width:1px;background:var(--border);margin:4px 2px;';
  el.appendChild(sep);

  const gridBtn = document.createElement('button');
  gridBtn.id = 'grid-toggle';
  gridBtn.title = 'Toggle grid (default off)';
  gridBtn.textContent = 'Grid';
  gridBtn.dataset.on = 'false';  // R15: default OFF
  gridBtn.style.cssText = 'padding:6px 12px;font-size:12px;margin:0;border:none;border-radius:4px;cursor:pointer;background:transparent;color:var(--text-dim);transition:all 0.15s;font-family:var(--mono-font);';
  gridBtn.addEventListener('click', () => {
    const isOn = gridBtn.dataset.on === 'true';
    gridBtn.dataset.on = isOn ? 'false' : 'true';
    gridBtn.style.background = isOn ? 'transparent' : 'var(--accent)';
    gridBtn.style.color = isOn ? 'var(--text-dim)' : 'var(--accent-text)';
    if (onGridToggle) onGridToggle(!isOn);
  });
  el.appendChild(gridBtn);

  const handle: ViewSwitcherHandle = {
    el,
    mode: initial,
    setOnChange(cb) { onChange = cb; },
    setOnGridToggle(cb) { onGridToggle = cb; },
    // internal helper exposed on handle so click handlers can switch without circular dep
    setMode(m: ViewMode) {
      handle.mode = m;
      modes.forEach((mm) => {
        const btn = buttons[mm];
        if (mm === m) {
          btn.style.background = 'var(--accent)';
          btn.style.color = 'var(--accent-text)';
        } else {
          btn.style.background = 'transparent';
          btn.style.color = 'var(--text-muted)';
        }
      });
    },
  };
  handle.setMode(initial);
  return handle;
}

// ---- Zoom all button ----
export interface ZoomAllHandle {
  el: HTMLElement;
  setOnClick: (cb: () => void) => void;
}

export function createZoomAllButton(): ZoomAllHandle {
  let onClick: (() => void) | null = null;
  const btn = document.createElement('button');
  btn.id = 'zoom-all-btn';
  btn.title = 'Fit camera to full model';
  btn.textContent = '⛶ Zoom All';
  btn.style.cssText = 'padding:6px 12px;font-size:12px;margin:0;border:none;border-radius:4px;cursor:pointer;background:var(--accent);color:var(--accent-text);transition:all 0.15s;font-family:var(--mono-font);';
  btn.addEventListener('click', () => {
    if (onClick) onClick();
  });
  return {
    el: btn,
    setOnClick(cb) { onClick = cb; },
  };
}

// ---- View preset buttons (Iso 1, Iso 2, Top, Bottom / Front, Back, Left, Right) — CAD quick viewpoints ----
export type ViewPreset = 'iso' | 'iso2' | 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom';

export interface ViewPresetsHandle {
  el: HTMLElement;
  setOnPreset: (cb: (preset: ViewPreset) => void) => void;
}

export function createViewPresets(): ViewPresetsHandle {
  const wrap = document.createElement('div');
  wrap.id = 'view-presets';
  wrap.style.cssText = 'display:flex;gap:2px;';
  let onPreset: ((p: ViewPreset) => void) | null = null;

  const btnStyle = 'padding:6px 10px;font-size:11px;border:none;border-radius:4px;cursor:pointer;background:rgba(255,255,255,0.7);color:var(--text);font-family:var(--mono-font);transition:all 0.15s;';
  const labels: Array<{ preset: ViewPreset; label: string; title: string }> = [
    { preset: 'iso',    label: '◆ Iso 1',  title: 'Isometric view 1 (Front-Right-Top)' },
    { preset: 'iso2',   label: '◇ Iso 2',  title: 'Isometric view 2 (Back-Left-Top)' },
    { preset: 'top',    label: '▲ Top',    title: 'Top-down view (+Z axis)' },
    { preset: 'bottom', label: '▼ Bottom', title: 'Bottom-up view (-Z axis)' },
    { preset: 'front',  label: '■ Front',  title: 'Front view (-Y looking toward +Y)' },
    { preset: 'back',   label: '□ Back',   title: 'Back view (+Y looking toward -Y)' },
    { preset: 'left',   label: '◀ Left',   title: 'Left-side view (-X axis)' },
    { preset: 'right',  label: '▶ Right',  title: 'Right-side view (+X axis)' },
  ];

  for (const item of labels) {
    const b = document.createElement('button');
    b.textContent = item.label;
    b.title = item.title;
    b.style.cssText = btnStyle;
    b.addEventListener('mouseenter', () => { b.style.background = 'var(--accent)'; b.style.color = 'var(--accent-text)'; });
    b.addEventListener('mouseleave', () => { b.style.background = 'rgba(255,255,255,0.7)'; b.style.color = 'var(--text)'; });
    b.addEventListener('click', () => { if (onPreset) onPreset(item.preset); });
    wrap.appendChild(b);
  }

  return {
    el: wrap,
    setOnPreset(cb) { onPreset = cb; },
  };
}

// ---- Watertight check toggle button ----
// Show/hide non-manifold edges (red). When ON, button turns red.
export interface WatertightHandle {
  el: HTMLElement;
  setOnToggle: (cb: (on: boolean) => void) => void;
}

export function createWatertightButton(initial: boolean = false): WatertightHandle {
  let onToggle: ((on: boolean) => void) | null = null;
  let isOn = initial;
  const btn = document.createElement('button');
  btn.id = 'watertight-toggle';
  btn.title = 'Show/hide non-manifold edges (red)';
  btn.textContent = '🔴 Watertight';
  const refresh = () => {
    btn.style.background = isOn ? 'var(--error)' : 'transparent';
    btn.style.color = isOn ? 'var(--accent-text)' : 'var(--text-muted)';
    btn.style.border = isOn ? '1px solid var(--error)' : '1px solid var(--border)';
  };
  refresh();
  btn.style.cssText += 'padding:6px 12px;font-size:12px;margin:0;border-radius:4px;cursor:pointer;transition:all 0.15s;font-family:var(--mono-font);';
  btn.addEventListener('click', () => {
    isOn = !isOn;
    refresh();
    if (onToggle) onToggle(isOn);
  });
  return {
    el: btn,
    setOnToggle(cb) { onToggle = cb; },
  };
}

// ---- Support detection toggle button ----
// Show/hide faces with overhang >45° (need support when printing). Orange.
export interface SupportHandle {
  el: HTMLElement;
  setOnToggle: (cb: (on: boolean) => void) => void;
}

export function createSupportButton(initial: boolean = false): SupportHandle {
  let onToggle: ((on: boolean) => void) | null = null;
  let isOn = initial;
  const btn = document.createElement('button');
  btn.id = 'support-toggle';
  btn.title = 'Show/hide faces needing support when 3D printing (>45° from vertical)';
  btn.textContent = '⚠️ Support';
  const refresh = () => {
    btn.style.background = isOn ? '#ff9900' : 'transparent';
    btn.style.color = isOn ? '#1a1a1a' : 'var(--text-muted)';
    btn.style.border = isOn ? '1px solid #ff9900' : '1px solid var(--border)';
  };
  refresh();
  btn.style.cssText += 'padding:6px 12px;font-size:12px;margin:0;border-radius:4px;cursor:pointer;transition:all 0.15s;font-family:var(--mono-font);';
  btn.addEventListener('click', () => {
    isOn = !isOn;
    refresh();
    if (onToggle) onToggle(isOn);
  });
  return {
    el: btn,
    setOnToggle(cb) { onToggle = cb; },
  };
}


// ---- TASK 23 (2026-09-30): runExport() REMOVED ----
//
// There used to be a `runExport()` here that POSTed to /api/export, polled the job and
// downloaded the result. It was never called from anywhere (the only reference was an import
// in main.ts), and its body hard-coded `format: 'stl'` + `quality: 'high'` while ignoring the
// user's actual choice in the UI - so anyone who wired it up would have seen a user pick
// "3MF (Bambu)" and silently receive an .stl.
//
// Export in this app is fully client-side: main.ts builds the file from the meshes already in
// the scene via exportMeshesTo3MF / exportMeshesToBinarySTL, and honours getExportFormat().
// The server-side /api/export endpoint still exists for other callers, so the route was left
// in place - only this unused client wrapper is gone.
//
// If server-side export is wanted later, write it against the current UI format selection
// rather than resurrecting this.

// ---- TASK 18 (2026-09-30): carry the server's reason, don't flatten it to a status code ----
//
// Every failure used to become `new Error('API HTTP ${status}')`, so the UI could not tell
// these apart even though the server sends quite different meanings:
//   429  queue full        -> "server busy, retry shortly"   (TASK 01 / TASK 10)
//   504  worker timed out  -> "this model is too heavy"      (TASK 04)
//   500  generation failed -> the model itself is broken
//   401  bad/missing key   -> configuration problem
// The server already returns `{error, details}` and a `Retry-After` header, so the
// information was being thrown away at the last step. This class keeps it.
// ApiError and preview transport are implemented in api-client.ts.
